import { c as _c } from "react/compiler-runtime";
import { Button, Checkbox, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Drawer, DrawerContent, DrawerTitle, DrawerTrigger, Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components';
import { useIsMobile } from '@repo/ui/hooks';
import { ArrowRightIcon, ChevronRightIcon, FilterIcon } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { isValidElement, type KeyboardEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import type { Column, ColumnDataType, DataTableFilterActions, FiltersState, FilterStrategy, OptionBasedColumnDataType } from '../core';
import { groupByCategory } from '../../table/column-meta';
import { findFilter } from '../core';
import { isAnyOf } from '../lib/array';
import { getColumn } from '../lib/helpers';
import { type Locale, t } from '../lib/i18n';
import { PaneTitle } from '../../table/components/pane-title';
import { FilterEditorAutoFocus } from './filter-editor-auto-focus';
import { FilterValueController } from './filter-value';
interface FilterSelectorProps<TData> {
  actions: DataTableFilterActions;
  columns: Column<TData>[];
  filters: FiltersState;
  locale?: Locale;
  strategy: FilterStrategy;
}
export const FilterSelector =
// SAFETY: the memo wrapper renders the component with the same props and is only used in JSX;
// memo's typings would drop the TData generic.
memo(__FilterSelector) as typeof __FilterSelector;
interface QuickSearchFiltersProps<TData> {
  actions: DataTableFilterActions;
  columns: Column<TData>[];
  filters: FiltersState;
  locale?: Locale;
  search?: string;
  strategy: FilterStrategy;
}
export function FilterableColumn(t0) {
  const $ = _c(24);
  const {
    column,
    pane: t1,
    setProperty
  } = t0;
  const pane = t1 === undefined ? false : t1;
  const itemRef = useRef(null);
  let t2;
  if ($[0] !== column) {
    t2 = () => {
      column.prefetchOptions();
      column.prefetchValues();
      column.prefetchFacetedUniqueValues();
      column.prefetchFacetedMinMaxValues();
    };
    $[0] = column;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const prefetch = t2;
  let t3;
  let t4;
  if ($[2] !== prefetch) {
    t3 = () => {
      const target = itemRef.current;
      if (!target) {
        return;
      }
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.type === "attributes") {
            const isSelected = target.getAttribute("data-selected") === "true";
            if (isSelected) {
              prefetch();
            }
          }
        }
      });
      observer.observe(target, {
        attributeFilter: ["data-selected"],
        attributes: true
      });
      return () => observer.disconnect();
    };
    t4 = [prefetch];
    $[2] = prefetch;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  const t5 = pane && "rounded-l-sm rounded-r-none data-[selected=true]:bg-muted";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("group [&>svg:last-child]:hidden", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== column.displayName) {
    t7 = [column.displayName];
    $[7] = column.displayName;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== column.id || $[10] !== setProperty) {
    t8 = () => setProperty(column.id);
    $[9] = column.id;
    $[10] = setProperty;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== column.displayName) {
    t9 = <span>{column.displayName}</span>;
    $[12] = column.displayName;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== pane) {
    t10 = pane ? null : <ArrowRightIcon className="ml-auto size-4 opacity-0 group-aria-selected:opacity-100" />;
    $[14] = pane;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== column.id || $[17] !== prefetch || $[18] !== t10 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t11 = <CommandItem className={t6} keywords={t7} onMouseEnter={prefetch} onSelect={t8} ref={itemRef} value={column.id} data-sentry-element="CommandItem" data-sentry-component="FilterableColumn" data-sentry-source-file="filter-selector.tsx">{t9}{t10}</CommandItem>;
    $[16] = column.id;
    $[17] = prefetch;
    $[18] = t10;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}

// Items match by their keywords (a filter's name, an option's label and value), not their id.
const matchesSearch = (_value: string, search: string, keywords?: string[]) => (keywords ?? []).join(' ').toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0;
function __FilterSelector<TData>({
  actions,
  columns,
  filters,
  locale = 'en',
  strategy
}: FilterSelectorProps<TData>) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [property, setProperty] = useState<string | undefined>(undefined);
  const [highlighted, setHighlighted] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLElement>(null);
  // Set while the user is moving into the editor: a click there, or Enter in the search.
  const enteringEditor = useRef(false);
  const categoryRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isMobile = useIsMobile();
  // Columns with categories list under them, like Add column; others keep one plain list.
  const groups = groupByCategory(columns, column => column.group);
  const grouped = columns.some(column => column.group);
  const [groupLabel, setGroupLabel] = useState('');
  const chosen = groups.find(entry => entry.group.label === groupLabel) ?? groups[0];
  const searching = value.trim().length > 0;
  // Three panes on desktop, like Add column: categories, the chosen category's filters (or while
  // searching, every category's), and the highlighted filter's editor, which follows the highlight
  // as Add column's preview does, so every filter is one click or arrow key away.
  const panes = grouped && !isMobile;
  const listed = panes && !searching && chosen ? [chosen] : groups;
  const column = property ? getColumn(columns, property) : undefined;
  const filter = property ? filters.find(f => f.columnId === property) : undefined;
  // Editors drawn at a set size rather than as a list that fills its space.
  const fixedSize = column ? isAnyOf<ColumnDataType>(column.type, ['amount', 'date', 'number']) : false;

  // Where the editor replaces the list, it starts from a cleared search.
  useEffect(() => {
    if (property && !panes) setValue('');
  }, [property, panes]);

  // The editor follows a highlighted filter; a highlighted quick-search value leaves it as it is.
  const highlight = (next: string) => {
    setHighlighted(next);
    const highlightedColumn = columns.find(column => column.id === next);
    if (!highlightedColumn) return;
    setProperty(next);
    // A filter found by search takes its category with it, so clearing the search lists it.
    if (highlightedColumn.group) setGroupLabel(highlightedColumn.group.label);
  };
  const quickSearch = <QuickSearchFilters actions={actions} columns={columns} filters={filters} locale={locale} search={value} strategy={strategy} />;
  const list = <>
      <CommandEmpty>{t('noresults', locale)}</CommandEmpty>
      {grouped ? <>
          {listed.map(({
        group,
        items
      }) => <CommandGroup className={cn(panes && 'pr-0')} heading={panes && !searching ? undefined : group.label} key={group.label}>
              {items.map(column => <FilterableColumn column={column} key={column.id} pane={panes} setProperty={setProperty} />)}
            </CommandGroup>)}
          <CommandGroup>{quickSearch}</CommandGroup>
        </> : <CommandGroup>
          {columns.map(column => <FilterableColumn column={column} key={column.id} setProperty={setProperty} />)}
          {quickSearch}
        </CommandGroup>}
    </>;
  const chooseCategory = (index: number) => {
    const entry = groups[index];
    if (!entry) return;
    setValue('');
    setGroupLabel(entry.group.label);
    highlight(entry.items[0]?.id ?? '');
    categoryRefs.current[index]?.focus();
  };

  // Up and Down step through the categories and choose each; Right goes to the search, whose
  // arrows move through the filters. Enter stays with the button, as the list would take it.
  const moveBetweenCategories = (index: number) => (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') return event.stopPropagation();
    if (event.key === 'ArrowRight') return inputRef.current?.focus();
    const step = {
      ArrowDown: 1,
      ArrowUp: -1
    }[event.key];
    if (!step) return;
    event.preventDefault();
    chooseCategory(index + step);
  };
  const editor = column ? <FilterValueController actions={actions} column={column} filter={filter} key={column.id} locale={locale} strategy={strategy} /> : null;
  const content = panes ? <Command
  // A click or arrow key highlights a filter and opens it beside the list; hovering leaves
  // the highlight alone. Enter in the search moves into the open filter.
  disablePointerSelection filter={matchesSearch} label={t('searchFilters', locale)} onKeyDown={event => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    // Enter on a quick-search value checks it; only a highlighted filter opens its editor.
    if (!columns.some(column => column.id === highlighted)) return;
    enteringEditor.current = true;
    // The editor is any filter's own markup, so its first field is found in the pane.
    editorRef.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
    enteringEditor.current = false;
  }} onValueChange={highlight} value={highlighted}>
      <CommandInput onValueChange={setValue} placeholder={t('searchFilters', locale)} ref={inputRef} value={value} />
      {/* Fixed panes, read left to right as Add column's are: the chosen category is a tab in
          the filter pane's colour and the highlighted filter a tab in the editor's, so each
          choice opens into the pane beside it. Moving never resizes the menu. */}
      <div className="mt-1 flex h-100 border-t">
        <nav aria-label={t('categories', locale)} className="w-40 shrink-0 overflow-y-auto bg-muted py-1 pl-1 shadow-[inset_-1px_0_var(--color-border)] lg:w-56">
          <PaneTitle>{t('category', locale)}</PaneTitle>
          {groups.map(({
          group,
          items
        }, index) => <button aria-pressed={!searching && group.label === chosen?.group.label} className="group/category flex w-full items-center gap-2 rounded-l-sm px-2 py-1.5 text-left text-sm text-muted-foreground outline-none hover:bg-popover/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset aria-pressed:bg-popover aria-pressed:text-foreground" key={group.label} onClick={() => chooseCategory(index)} onKeyDown={moveBetweenCategories(index)} ref={button => {
          categoryRefs.current[index] = button;
        }} type="button">
              <span className="flex-1 truncate">{group.label}</span>{' '}
              <span className="text-xs tabular-nums">{items.length}</span>
              <ChevronRightIcon aria-hidden className="size-3.5 opacity-0 group-aria-pressed/category:opacity-60" />
            </button>)}
        </nav>
        {/* Narrower side panes below `lg` keep room for the 300px number and amount editors. */}
        <div className="flex w-40 shrink-0 flex-col shadow-[inset_-1px_0_var(--color-border)] lg:w-56">
          <PaneTitle className="px-3">{t('filters', locale)}</PaneTitle>
          <CommandList className="max-h-none flex-1 pb-1 pl-1" label={t('filters', locale)}>
            {list}
          </CommandList>
        </div>
        {/* The editor keeps its own keys: its lists must not move the highlight here. */}
        <section aria-label={column?.displayName ?? t('filter', locale)} className="flex min-w-0 flex-1 flex-col bg-muted"
      // cmdk focuses its own search whenever its highlight changes while any cmdk search has
      // focus, so an editor's list would take focus from the filter search as it opens. Only
      // a click or Enter moves into the editor; otherwise focus goes back to the search.
      onFocus={event => {
        if (!enteringEditor.current && event.relatedTarget === inputRef.current) {
          inputRef.current?.focus();
        }
        enteringEditor.current = false;
      }} onKeyDown={event => {
        // Escape still reaches the popover, to close it.
        if (event.key !== 'Escape') event.stopPropagation();
      }} onPointerDown={() => {
        // A click's focus lands in this task; one that lands nowhere mustn't leave this set.
        enteringEditor.current = true;
        setTimeout(() => {
          enteringEditor.current = false;
        });
      }} ref={editorRef}>
          <PaneTitle className="px-3">{column?.displayName ?? t('filter', locale)}</PaneTitle>
          <div className="flex min-h-0 flex-1 flex-col px-3 pb-3">
            {editor ? <FilterEditorAutoFocus value={false}>
                {/* One surface fills the pane, so every filter sits in the same frame. Lists fill
                    it; a calendar or number field keeps the size it has in its own popover. */}
                <div className={cn('min-h-0 flex-1 overflow-y-auto rounded-md border bg-popover', fixedSize && 'flex justify-center py-2')}>
                  <div className={fixedSize ? 'w-fit max-w-full' : undefined}>{editor}</div>
                </div>
              </FilterEditorAutoFocus> : <p className="text-sm text-muted-foreground">{t('chooseFilter', locale)}</p>}
          </div>
        </section>
      </div>
    </Command> : property && editor || <Command className={isMobile ? 'min-h-0 flex-1' : undefined} filter={matchesSearch} loop>
        <CommandInput onValueChange={setValue} placeholder={t('search', locale)} ref={inputRef} value={value} />
        <CommandList className={isMobile ? 'max-h-none min-h-0 flex-1' : 'max-h-[min(24rem,calc(var(--available-height)-3.5rem))]'}>
          {list}
        </CommandList>
      </Command>;
  const handleOpenChange = (nextOpen: boolean) => {
    // Opening starts from a cleared search and the first filter, its editor open beside it.
    // Resetting on open leaves no timer behind to clear what the user types next.
    if (nextOpen) {
      setValue('');
      setProperty(undefined);
      setHighlighted('');
    }
    setOpen(nextOpen);
  };
  const trigger = <Button className="h-9 w-9" variant="outline">
      <FilterIcon className="size-4" />
    </Button>;
  if (isMobile) {
    return <Drawer onOpenChange={handleOpenChange} open={open}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="pb-[env(safe-area-inset-bottom)]">
          <DrawerTitle className="sr-only">{t('filter', locale)}</DrawerTitle>
          <div className="flex min-h-0 flex-1 flex-col">{content}</div>
        </DrawerContent>
      </Drawer>;
  }
  return <Popover onOpenChange={handleOpenChange} open={open} data-sentry-element="Popover" data-sentry-component="__FilterSelector" data-sentry-source-file="filter-selector.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="filter-selector.tsx">{trigger}</PopoverTrigger>
      <PopoverContent align="start" className={panes ? 'w-[min(56rem,calc(100vw-2rem))] p-0' : 'w-fit p-0'} side="bottom" data-sentry-element="PopoverContent" data-sentry-source-file="filter-selector.tsx">
        {content}
      </PopoverContent>
    </Popover>;
}
export const QuickSearchFilters =
// SAFETY: the memo wrapper renders the component with the same props and is only used in JSX;
// memo's typings would drop the TData generic.
memo(__QuickSearchFilters) as typeof __QuickSearchFilters;
function __QuickSearchFilters<TData>({
  actions,
  columns,
  filters,
  locale = 'en',
  search,
  strategy
}: QuickSearchFiltersProps<TData>) {
  const cols = useMemo(() => columns.filter((c): c is Column<TData, OptionBasedColumnDataType> => isAnyOf<ColumnDataType>(c.type, ['option', 'multiOption'])), [columns]);
  if (!search || search.trim().length < 2) return null;
  return <>
      {cols.map(column => {
      const filter = findFilter(filters, column);
      const options = column.getOptions();
      const optionsCount = column.getFacetedUniqueValues();
      function handleOptionSelect(value: string, check: boolean) {
        if (check) actions.addFilterValue(column, [value]);else actions.removeFilterValue(column, [value]);
      }
      return <React.Fragment key={column.id}>
            {options.map(v => {
          const checked = Boolean(filter?.values.includes(v.value));
          const count = optionsCount?.get(v.value) ?? 0;
          return <CommandItem className="group" key={v.value} keywords={[v.label, v.value]} onSelect={() => {
            handleOptionSelect(v.value, !checked);
          }} value={v.value}>
                  <div className="group flex items-center gap-1.5">
                    <Checkbox checked={checked} />
                    <div className="flex w-4 items-center justify-center">
                      {v.icon && (isValidElement(v.icon) ? v.icon : <v.icon className="size-4 text-primary" />)}
                    </div>
                    <div className="flex items-center gap-0.5">
                      <span className="text-muted-foreground">{column.displayName}</span>
                      <ChevronRightIcon className="size-3.5 text-muted-foreground/75" />
                      <span>
                        {v.label}
                        <sup className={cn(!optionsCount && 'hidden', 'ml-0.5 tracking-tight text-muted-foreground tabular-nums', count === 0 && 'slashed-zero')}>
                          {count < 100 ? count : '100+'}
                        </sup>
                      </span>
                    </div>
                  </div>
                </CommandItem>;
        })}
          </React.Fragment>;
    })}
    </>;
}